/**
 * Taiba Luxury Wishlist Manager
 * Manages wishlist state, header count badges, product animations, and localStorage synchronization.
 */
class WishlistManager {
  constructor() {
    this.wishlistKey = 'taiba_luxury_wishlist';
    this.wishlist = this.getWishlist();
    this.grid = document.getElementById('wishlist-grid');
    this.emptyState = document.getElementById('wishlist-empty');

    this.init();
  }

  /**
   * Retrieves wishlist items from localStorage, normalizing string handles to item objects.
   * @returns {Array<Object>}
   */
  getWishlist() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.wishlistKey) || '[]');
      return stored.map((item) => {
        if (typeof item === 'string') {
          return {
            handle: item,
            title: item.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            url: `/products/${item}`,
            image: '',
            formatted_price: ''
          };
        }
        return item;
      });
    } catch (e) {
      return [];
    }
  }

  /**
   * Returns an array of handles currently in the wishlist.
   * @returns {string[]}
   */
  getHandles() {
    return this.wishlist.map((item) => (typeof item === 'string' ? item : item.handle)).filter(Boolean);
  }

  /**
   * Checks if a handle is in the wishlist.
   * @param {string} handle
   * @returns {boolean}
   */
  hasItem(handle) {
    if (!handle) return false;
    return this.getHandles().includes(handle);
  }

  /**
   * Saves current wishlist to localStorage, updates badges, and dispatches an event.
   */
  saveWishlist() {
    localStorage.setItem(this.wishlistKey, JSON.stringify(this.wishlist));
    this.updateBadge();
    this.render();
    window.dispatchEvent(
      new CustomEvent('wishlist:updated', {
        detail: {
          wishlist: this.wishlist,
          count: this.wishlist.length
        }
      })
    );
  }

  /**
   * Adds an item object or handle to the wishlist.
   * @param {Object|string} itemData
   */
  addToWishlist(itemData) {
    const handle = typeof itemData === 'string' ? itemData : itemData.handle;
    if (!handle) return;

    if (!this.hasItem(handle)) {
      const itemObj =
        typeof itemData === 'object'
          ? itemData
          : {
              handle: handle,
              title: handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              url: `/products/${handle}`,
              image: '',
              formatted_price: ''
            };

      this.wishlist.push(itemObj);
      this.saveWishlist();

      // If missing image or price (e.g. legacy or minimal data), fetch in background to enrich
      if (!itemObj.image || !itemObj.formatted_price) {
        this.enrichItemData(handle);
      }
    }
  }

  /**
   * Removes an item from the wishlist by handle.
   * @param {string} handle
   */
  removeFromWishlist(handle) {
    if (!handle) return;
    this.wishlist = this.wishlist.filter((item) => {
      const h = typeof item === 'string' ? item : item.handle;
      return h !== handle;
    });
    this.saveWishlist();
    this.markWishlistButtons();
  }

  /**
   * Background fetch to enrich item data if needed.
   * @param {string} handle
   */
  async enrichItemData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return;
      const product = await response.json();
      const index = this.wishlist.findIndex((i) => (typeof i === 'string' ? i : i.handle) === handle);
      if (index !== -1) {
        const price = new Intl.NumberFormat('en-AE', {
          style: 'currency',
          currency: product.currency || 'AED'
        }).format(product.price / 100);

        this.wishlist[index] = {
          ...this.wishlist[index],
          title: product.title,
          image: product.featured_image || '',
          price: product.price,
          formatted_price: price,
          url: product.url
        };
        localStorage.setItem(this.wishlistKey, JSON.stringify(this.wishlist));
        window.dispatchEvent(
          new CustomEvent('wishlist:updated', {
            detail: { wishlist: this.wishlist, count: this.wishlist.length }
          })
        );
      }
    } catch (e) {
      // Ignore background fetch errors
    }
  }

  /**
   * Updates all wishlist count badges across the page (header icon, drawer header).
   * @param {boolean} animate - Whether to animate the badge pop effect.
   */
  updateBadge(animate = false) {
    const count = this.wishlist.length;
    const headerBadges = document.querySelectorAll('.wishlist-count-badge, #wishlist-count-badge');
    const drawerBadges = document.querySelectorAll('#wishlist-drawer-badge');

    headerBadges.forEach((badge) => {
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.style.display = 'inline-flex';
        if (animate) {
          badge.classList.remove('wishlist-badge-pop');
          void badge.offsetWidth;
          badge.classList.add('wishlist-badge-pop');
          badge.addEventListener('animationend', () => {
            badge.classList.remove('wishlist-badge-pop');
          }, { once: true });
        }
      } else {
        badge.textContent = '0';
        badge.style.display = 'none';
      }
    });

    drawerBadges.forEach((badge) => {
      if (count > 0) {
        badge.textContent = count > 99 ? '99+' : String(count);
        badge.style.display = 'inline-flex';
        badge.classList.remove('visually-hidden');
      } else {
        badge.textContent = '0';
        badge.style.display = 'none';
      }
    });
  }

  /**
   * Animates the Add-to-Wishlist flow like Add-to-Cart.
   * 1. Pulses the clicked button.
   * 2. Flying element travels from the button to the header wishlist icon.
   * 3. On arrival: header icon bumps, badge pops and count increments.
   * @param {HTMLElement} btn
   */
  animateAdd(btn) {
    // 1. Button pulse feedback
    btn.classList.remove('wishlist-animating');
    void btn.offsetWidth;
    btn.classList.add('wishlist-animating');
    btn.addEventListener('animationend', () => {
      btn.classList.remove('wishlist-animating');
    }, { once: true });

    // 2. Find target header icon
    const headerIcons = Array.from(document.querySelectorAll('.action__wishlist'));
    const headerIcon = headerIcons.find((el) => el.offsetParent !== null) || headerIcons[0];

    if (!headerIcon) {
      this.updateBadge(true);
      return;
    }

    const btnRect = btn.getBoundingClientRect();
    const iconRect = headerIcon.getBoundingClientRect();

    const flyEl = document.createElement('div');
    flyEl.className = 'wishlist-fly-heart';

    const imgUrl = btn.dataset.productImage;
    if (imgUrl) {
      flyEl.innerHTML = `
        <div style="width: 36px; height: 36px; border-radius: 50%; overflow: hidden; position: relative; border: 2px solid #b28b5d; background: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.2);">
          <img src="${imgUrl}" alt="Wishlist item" style="width: 100%; height: 100%; object-fit: cover;">
          <span style="position: absolute; bottom: 0; right: 0; width: 14px; height: 14px; background: #c93b3b; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 8px;">❤</span>
        </div>
      `;
    } else {
      flyEl.innerHTML = `
        <div style="width: 32px; height: 32px; border-radius: 50%; background: #ffffff; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(178,139,93,0.35); border: 1.5px solid #b28b5d;">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="#c93b3b" stroke="#c93b3b" stroke-width="1">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </div>
      `;
    }

    const startX = btnRect.left + btnRect.width / 2;
    const startY = btnRect.top + btnRect.height / 2;
    const endX = iconRect.left + iconRect.width / 2;
    const endY = iconRect.top + iconRect.height / 2;

    flyEl.style.position = 'fixed';
    flyEl.style.left = `${startX}px`;
    flyEl.style.top = `${startY}px`;
    flyEl.style.setProperty('--fly-x', `${endX - startX}px`);
    flyEl.style.setProperty('--fly-y', `${endY - startY}px`);
    flyEl.style.zIndex = '999999';
    flyEl.style.pointerEvents = 'none';

    document.body.appendChild(flyEl);

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (flyEl.parentNode) flyEl.remove();

      // 3. Header icon bump and badge pop
      headerIcon.classList.remove('wishlist-bump');
      void headerIcon.offsetWidth;
      headerIcon.classList.add('wishlist-bump');
      headerIcon.addEventListener('animationend', () => {
        headerIcon.classList.remove('wishlist-bump');
      }, { once: true });

      this.updateBadge(true);
    };

    flyEl.addEventListener('animationend', cleanup, { once: true });
    setTimeout(cleanup, 700);
  }

  /**
   * Highlights or resets all wishlist buttons matching current wishlist state.
   */
  markWishlistButtons() {
    const handles = this.getHandles();
    document.querySelectorAll('.js-wishlist-btn').forEach((btn) => {
      const handle = btn.dataset.productHandle;
      const textSpan = btn.querySelector('.product-wishlist-btn-text');
      const isTargetActive = Boolean(handle && handles.includes(handle));

      if (isTargetActive) {
        if (!btn.classList.contains('is-active')) btn.classList.add('is-active');
        if (textSpan && textSpan.textContent !== 'In Wishlist') textSpan.textContent = 'In Wishlist';
      } else {
        if (btn.classList.contains('is-active')) btn.classList.remove('is-active');
        if (textSpan && textSpan.textContent !== 'Add to Wishlist') textSpan.textContent = 'Add to Wishlist';
      }
    });
  }

  /**
   * Encodes HTML special characters to prevent DOM-based XSS.
   * @param {string} str
   * @returns {string}
   */
  escapeHTML(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Validates and sanitizes URLs to ensure only safe relative paths or http/https links are rendered.
   * @param {string} url
   * @returns {string}
   */
  sanitizeURL(url) {
    if (!url) return '#';
    const clean = String(url).trim();
    if (clean.startsWith('/') || clean.startsWith('http://') || clean.startsWith('https://')) {
      return clean.replace(/"/g, '%22').replace(/'/g, '%27');
    }
    return '#';
  }

  /**
   * Renders the dedicated /pages/wishlist page if present.
   */
  render() {
    if (!this.grid) return;

    if (this.wishlist.length === 0) {
      this.grid.style.display = 'none';
      if (this.emptyState) this.emptyState.style.display = 'block';
      this.grid.innerHTML = '';
      return;
    }

    this.grid.style.display = 'grid';
    if (this.emptyState) this.emptyState.style.display = 'none';
    this.grid.innerHTML = '';

    this.wishlist.forEach((item) => {
      const handle = typeof item === 'string' ? item : item.handle;
      const title = item.title || handle;
      const price = item.formatted_price || (item.price ? (item.price / 100).toFixed(2) + ' AED' : '');
      const imgUrl = item.image || '';
      const url = item.url || `/products/${handle}`;

      const safeHandle = this.escapeHTML(handle);
      const safeTitle = this.escapeHTML(title);
      const safePrice = this.escapeHTML(price);
      const safeImgUrl = this.sanitizeURL(imgUrl);
      const safeUrl = this.sanitizeURL(url);

      const itemHTML = `
        <div class="grid__item wishlist-item">
          <button class="wishlist-remove" data-handle="${safeHandle}" aria-label="Remove ${safeTitle} from wishlist">
            <svg viewBox="0 0 14 14" width="14" height="14"><path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/></svg>
          </button>
          <div class="card-wrapper underline-links-hover">
            <div class="card card--standard card--media">
              <div class="card__inner">
                <a href="${safeUrl}" class="card__media">
                  <div class="media media--transparent media--hover-effect" style="padding-bottom: 100%;">
                    ${safeImgUrl ? `<img src="${safeImgUrl}" alt="${safeTitle}" loading="lazy">` : `<div style="width:100%;height:100%;background:#f5f5f5;"></div>`}
                  </div>
                </a>
              </div>
              <div class="card__content">
                <div class="card__information text-center">
                  <h3 class="card__heading h5">
                    <a href="${safeUrl}" class="full-unstyled-link">${safeTitle}</a>
                  </h3>
                  <div class="card-information">
                    <div class="price">
                      <div class="price__regular">
                        <span class="price-item price-item--regular">${safePrice}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      this.grid.insertAdjacentHTML('beforeend', itemHTML);
    });

    this.grid.querySelectorAll('.wishlist-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = e.currentTarget.dataset.handle;
        this.removeFromWishlist(handle);
      });
    });
  }

  /**
   * Initializes event listeners and sets initial state.
   */
  init() {
    this.updateBadge();
    this.render();
    this.markWishlistButtons();

    // Global delegated click listener for all wishlist buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-wishlist-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();

        const handle = btn.dataset.productHandle;
        if (!handle) return;

        if (this.hasItem(handle)) {
          this.removeFromWishlist(handle);
          btn.classList.remove('is-active');
          const textSpan = btn.querySelector('.product-wishlist-btn-text');
          if (textSpan) textSpan.textContent = 'Add to Wishlist';
        } else {
          const itemData = {
            handle: handle,
            id: btn.dataset.productId || '',
            title: btn.dataset.productTitle || handle,
            price: btn.dataset.productPrice || '',
            formatted_price: btn.dataset.productFormattedPrice || '',
            image: btn.dataset.productImage || '',
            url: btn.dataset.productUrl || `/products/${handle}`
          };

          this.addToWishlist(itemData);
          btn.classList.add('is-active');
          const textSpan = btn.querySelector('.product-wishlist-btn-text');
          if (textSpan) textSpan.textContent = 'In Wishlist';

          this.animateAdd(btn);
        }
      }
    });

    // Update buttons on dynamic Shopify events
    document.addEventListener('shopify:section:load', () => this.markWishlistButtons());
    document.addEventListener('quick-add:loaded', () => this.markWishlistButtons());
  }
}

// Instantiate and expose globally
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
